import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { reactHookForm } from '@yola/ws-ui';
import segment from 'src/js/modules/analytics/segment';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import helpers from '../helpers';
import constants from '../constants';
import ContactFormEditFieldDialog from '../components/contact-form-edit-field-dialog';

const { useForm } = reactHookForm;

const {
  track,
  constants: { events, triggerIds },
  helpers: { getDefaultTraits },
} = segment;
const {
  common: { ALL_FILE_TYPES_VALUE },
  fieldControlNames,
  fieldControlCategories,
  numberOfFilesAttachment,
} = constants;

const getFieldControlCategory = (fieldType, fieldTypes) => {
  if (
    fieldType === fieldTypes.dropdown ||
    fieldType === fieldTypes.radio ||
    fieldType === fieldTypes.checkbox
  ) {
    return fieldControlCategories.OPTIONS;
  }

  if (fieldType === fieldTypes.time) {
    return fieldControlCategories.TIME;
  }

  if (fieldType === fieldTypes.attachfile) {
    return fieldControlCategories.FILE;
  }

  return fieldControlCategories.BASIC;
};

const getFieldControlsByType = (type, fieldTypes) => {
  const fieldControlType = getFieldControlCategory(type, fieldTypes);

  if (fieldControlType === fieldControlCategories.OPTIONS) {
    return [
      fieldControlNames.LABEL,
      fieldControlNames.OPTIONS,
      ...(type === fieldTypes.dropdown ? [] : [fieldControlNames.REQUIRED]),
    ];
  }

  if (fieldControlType === fieldControlCategories.TIME) {
    return [
      fieldControlNames.LABEL,
      fieldControlNames.PLACEHOLDER,
      fieldControlNames.TIME_FORMAT,
      fieldControlNames.REQUIRED,
    ];
  }

  if (fieldControlType === fieldControlCategories.FILE) {
    return [
      fieldControlNames.LABEL,
      fieldControlNames.BUTTON_TEXT,
      fieldControlNames.FILE_TYPE,
      fieldControlNames.NUMBER_OF_FILES,
      fieldControlNames.REQUIRED,
    ];
  }

  return [fieldControlNames.LABEL, fieldControlNames.PLACEHOLDER, fieldControlNames.REQUIRED];
};

const getFieldControlValue = (
  controlName,
  type,
  fieldTypes,
  {
    label = '',
    placeholder = '',
    options = [],
    buttonText = '',
    fileType = ALL_FILE_TYPES_VALUE,
    numberOfFiles = numberOfFilesAttachment.SINGLE,
    timeFormat = helpers.getTimeFormat(),
    required = false,
  }
) => {
  switch (controlName) {
    case fieldControlNames.LABEL:
      return label;
    case fieldControlNames.PLACEHOLDER:
      return placeholder;
    case fieldControlNames.OPTIONS:
      return options.length
        ? options
        : helpers.getOptionsFieldControlDefaultValue(type, fieldTypes);
    case fieldControlNames.BUTTON_TEXT:
      return buttonText;
    case fieldControlNames.FILE_TYPE:
      return fileType;
    case fieldControlNames.NUMBER_OF_FILES:
      return numberOfFiles;
    case fieldControlNames.TIME_FORMAT:
      return timeFormat;
    case fieldControlNames.REQUIRED:
      return required;
    default:
      return '';
  }
};

const getFieldControlValuesByType = (type, { fieldTypes, fieldData }) => {
  const fieldControls = getFieldControlsByType(type, fieldTypes);

  return fieldControls.reduce(
    (acc, controlName) => ({
      ...acc,
      [controlName]: getFieldControlValue(controlName, type, fieldTypes, fieldData),
    }),
    {}
  );
};

function ContactFormEditFieldContainer({
  elementId,
  field,
  fields,
  modalPosition: modalPositionProp,
  onSubmit,
  onCancel,
  onDialogMainAction,
  onDialogCancel,
}) {
  const { id, type, category, label } = field;

  const isNewField = !id;
  const placeholders = helpers.getFieldTypePlaceholders(type);
  const fieldTypes = helpers.getFieldTypes();
  const fieldControlCategory = getFieldControlCategory(type, fieldTypes);
  const defaultFieldControlValues = getFieldControlValuesByType(type, {
    fieldTypes,
    fieldData: field,
  });

  const [isOverlayClicked, setIsOverlayClicked] = useState(false);
  const [modalPosition, setModalPosition] = useState(modalPositionProp);

  const {
    control,
    getValues,
    trigger: triggerValidation,
    formState: { dirtyFields, isValid },
  } = useForm({
    mode: 'all',
    defaultValues: defaultFieldControlValues,
  });

  const handleSubmit = () => {
    if (!isValid) {
      triggerValidation();
      return;
    }

    const updatedFieldValues = getValues();
    const updatedField = isNewField
      ? helpers.generateNewFieldData(
          {
            ...field,
            ...updatedFieldValues,
          },
          fields.length + 1
        )
      : {
          ...field,
          ...updatedFieldValues,
        };

    const traits = isNewField
      ? {
          dialogType: dialogTypes.CONTACT_FORM_NEW_ITEM,
          categoryId: category,
          numberOfOptions:
            fieldControlCategory === fieldControlCategories.OPTIONS
              ? updatedField.options.length
              : null,
        }
      : {
          dialogType: dialogTypes.CONTACT_FORM_EDIT_ITEM,
          itemCurrentLabel: label || null,
          labelAdjusted: Object.keys(dirtyFields).includes(fieldControlNames.LABEL),
          isRequiredAdjusted: Object.keys(dirtyFields).includes(fieldControlNames.REQUIRED),
        };

    onDialogMainAction({
      ...getDefaultTraits(elementId),
      itemId: updatedField.id || null,
      itemType: type,
      itemNewLabel: updatedField.label,
      isRequired: !!updatedField.required,
      triggerId: isOverlayClicked ? triggerIds.DIALOG_BACKGROUND : triggerIds.SUBMIT_BUTTON,
      ...traits,
    });

    onSubmit({
      field: updatedField,
      modalPosition,
    });
    setIsOverlayClicked(false);
  };

  const handleCancel = () => {
    const updatedFieldValues = getValues();

    const traits = isNewField
      ? {
          dialogType: dialogTypes.CONTACT_FORM_NEW_ITEM,
          categoryId: category,
        }
      : {
          dialogType: dialogTypes.CONTACT_FORM_EDIT_ITEM,
          itemId: id,
          itemCurrentLabel: label || null,
          itemNewLabel: updatedFieldValues.label || null,
        };

    onDialogCancel({
      ...getDefaultTraits(elementId),
      itemType: type,
      ...traits,
    });
    onCancel({
      modalPosition,
    });
  };

  const handleChangeRequiredFieldControl = () => {
    if (!isNewField) {
      track(events.CONTACT_FORM_EDIT_ITEM_MAKE_REQUIRED_AREA_CLICKED, {
        ...getDefaultTraits(elementId),
        dialogId: dialogTypes.CONTACT_FORM_EDIT_ITEM,
        itemId: id,
        itemType: type,
      });
    }
  };

  const handleModalOverlayClick = () => {
    setIsOverlayClicked(true);
    handleSubmit();
  };

  const handleModalPositionUpdate = (event, { x, y }) => {
    setModalPosition({ offsetX: x, offsetY: y });
  };

  useEffect(() => {
    if (isNewField) {
      track(events.CONTACT_FORM_ADD_ITEM_DIALOG_DISPLAYED, {
        ...getDefaultTraits(elementId),
        dialogId: dialogTypes.CONTACT_FORM_NEW_ITEM,
        itemType: type,
        categoryId: category,
      });
    } else {
      track(events.CONTACT_FORM_EDIT_ITEM_DIALOG_DISPLAYED, {
        ...getDefaultTraits(elementId),
        dialogId: dialogTypes.CONTACT_FORM_EDIT_ITEM,
        itemType: type,
        itemId: id,
        itemCurrentLabel: label || null,
        itemNewLabel: label,
      });
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <ContactFormEditFieldDialog
      control={control}
      field={field}
      fieldTypes={fieldTypes}
      fieldControlCategory={fieldControlCategory}
      placeholders={placeholders}
      type={type}
      modalPosition={modalPosition}
      isNewField={isNewField}
      onChangeRequiredFieldControl={handleChangeRequiredFieldControl}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onModalOverlayClick={handleModalOverlayClick}
      onModalPositionUpdate={handleModalPositionUpdate}
    />
  );
}

ContactFormEditFieldContainer.propTypes = {
  elementId: PropTypes.string.isRequired,
  field: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    category: PropTypes.string,
    label: PropTypes.string,
    buttonText: PropTypes.string,
    placeholder: PropTypes.string,
  }).isRequired,
  fields: PropTypes.array.isRequired,
  modalPosition: PropTypes.shape({
    offsetX: PropTypes.number.isRequired,
    offsetY: PropTypes.number.isRequired,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default ContactFormEditFieldContainer;
